import React, { useState, useEffect, useRef } from 'react';
import { Grid, Stack, Box } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { renderWeekPickerDay } from '../../common/ui/qvshop/components/utils/DatePickerUtils';
import { getPickersLocale } from '../../common/ui/qvshop/components/utils/DatePickerLocale';
import { getAvailabilityCalendarForMonth, selectProduct } from '../../common/store/productSlice';
import { selectLocation } from '../../common/store/filtersSlice';
import DateInfoPopover from './DateInfoPopover';

import QVContainer from '../../common/ui/qvshop/components/QVContainer';

import {
    selectClosingDays,
    selectSemesterSettings,
    selectShowReservationsOnHoverInAvailabilityCalendar,
    selectWorkingHours
} from '../../common/store/settingsSlice';
import { ShopDatesInterval } from '../../common/utils/ShopDatesInterval';
import RecurrenceDays from '../../common/ui/qvshop/components/recurringIntervalSelector/utils/RecurrenceDays';

// Custom styled DateCalendar to make selection transparent
const StyledDateCalendar = styled(DateCalendar)(({ theme }) => ({
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiPickersSlideTransition-root': {
        width: '100%',
        minHeight: 240,
        flex: '1 1 auto',
    },
    '& .MuiPickersCalendarHeader-root': {
        flex: '0 0 auto',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    '& .MuiDayCalendar-root, & .MuiDayCalendar-monthContainer': {
        width: '100%',
    },
    '& .MuiDayCalendar-root': {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
    },
    '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
        justifyContent: 'space-between',
        width: '100%',
    },
    '& .MuiDayCalendar-header span, & .MuiDayCalendar-weekContainer > *': {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
    },
    '& .MuiDayCalendar-monthContainer': {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
    },
    '& .MuiDayCalendar-weekContainer': {
        flex: '1 1 0',
        alignItems: 'center',
    },
    '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: 'transparent',
        border: 'none'
    },
    '& .MuiPickersDay-root:hover': {
        backgroundColor: 'transparent',
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
        backgroundColor: 'transparent',
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-root:focus, & .MuiPickersDay-root.Mui-focused': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-root.Mui-highlighted': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-today': {
        border: 'none'
    }
}));

const AvailabilityCalendar = () => {
    const product = useSelector(selectProduct);
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const pickersLocale = getPickersLocale(i18n.language);

    const showReservationsOnHoverInAvailabilityCalendar = useSelector(selectShowReservationsOnHoverInAvailabilityCalendar);
    const location = useSelector(selectLocation);
    const workingHours = useSelector(selectWorkingHours);
    const semesterSettings = useSelector(selectSemesterSettings);
    const closingDays = useSelector(selectClosingDays);

    const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
    const nextMonth = currentMonth.add(1, 'month');

    // Track the month/year values to prevent unnecessary re-fetches
    const [fetchedLeftMonths, setFetchedLeftMonths] = useState({});
    const [fetchedRightMonths, setFetchedRightMonths] = useState({});

    const [hoveredDay, setHoveredDay] = useState(null);
    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
    const [calendarWidth, setCalendarWidth] = useState(0);
    const calendarRef = useRef(null);
    const showTwoMonths = calendarWidth >= 560;

    const shouldDisableDate = (dayToCheck) => {
        if (!workingHours || !semesterSettings) {
            return false;
        }

        const pickupAvailable = ShopDatesInterval.isDateAvailable(
            workingHours,
            semesterSettings,
            dayToCheck,
            RecurrenceDays.WORKING_HOURS_KEY_FOR_PICKUP,
            false,
            closingDays
        );
        const dropoffAvailable = ShopDatesInterval.isDateAvailable(
            workingHours,
            semesterSettings,
            dayToCheck,
            RecurrenceDays.WORKING_HOURS_KEY_FOR_DROPOFF,
            false,
            closingDays
        );

        return !pickupAvailable && !dropoffAvailable;
    };

    const handleDayHoverOut = () => {
        setHoveredDay(null);
    }

    const segmentsForDay = (day) => {
        if(!day || !product) {
            return [];
        }
        const formattedDate = day.format("YYYY-MM-DD");
        const monthData = day.month() === currentMonth.month() && day.year() === currentMonth.year()
            ? product?.month_left?.[product.product_id]
            : product?.month_right?.[product.product_id];
        return monthData?.[formattedDate]?.segments || [];
    };

    const handleDayHover = (day, event) => {
        const hasSegments = segmentsForDay(day).length > 0;
        if (!showReservationsOnHoverInAvailabilityCalendar && !hasSegments) {
            return;
        }

        if (event) {
            // Calculate position for the popover
            const rect = event.currentTarget.getBoundingClientRect();
            const calendarRect = calendarRef.current.getBoundingClientRect();
            
            // Position the popover to the right and slightly below the day cell
            setPopoverPosition({
                x: rect.left - calendarRect.left + 40, // Position to the right of the day cell
                y: rect.top - calendarRect.top + 40     // Slightly above the top of the day cell
            });

            setHoveredDay(day);
        }
    };

    const segmentsForHoveredDay = segmentsForDay(hoveredDay);

    useEffect(() => {
        if (!calendarRef.current) {
            return;
        }

        const updateCalendarWidth = () => {
            if (calendarRef.current) {
                setCalendarWidth(calendarRef.current.getBoundingClientRect().width);
            }
        };

        updateCalendarWidth();
        const animationFrame = requestAnimationFrame(updateCalendarWidth);
        let resizeObserver;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(([entry]) => {
                setCalendarWidth(entry.contentRect.width);
            });

            resizeObserver.observe(calendarRef.current);
        }

        window.addEventListener('resize', updateCalendarWidth);

        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', updateCalendarWidth);
            resizeObserver && resizeObserver.disconnect();
        };
    }, [product]);
    
    useEffect(() => {
        if (product) {
            // Calendar API data depends on product, location, month and year.
            const currentMonthKey = `${product.product_id}-${location}-${currentMonth.format('MM')}-${currentMonth.format('YYYY')}`;
            
            // Only fetch if we haven't already fetched this month for the left calendar
            if (!fetchedLeftMonths[currentMonthKey]) {
                console.log('Fetching left calendar for:', currentMonthKey);

                dispatch(getAvailabilityCalendarForMonth({
                    month: currentMonth.format('MM'),
                    year: currentMonth.format('YYYY'),
                    product: product,
                    field: 'month_left'
                }));
                
                // Mark this month as fetched for left calendar
                setFetchedLeftMonths(prev => ({
                    ...prev,
                    [currentMonthKey]: true
                }));
            }
        }
    }, [dispatch, product, location, currentMonth, fetchedLeftMonths]);

    useEffect(() => {
        if (product && showTwoMonths) {
            // Calendar API data depends on product, location, month and year.
            const nextMonthKey = `${product.product_id}-${location}-${nextMonth.format('MM')}-${nextMonth.format('YYYY')}`;
            
            // Only fetch if we haven't already fetched this month for the right calendar
            if (!fetchedRightMonths[nextMonthKey]) {
                console.log('Fetching right calendar for:', nextMonthKey);

                dispatch(getAvailabilityCalendarForMonth({
                    month: nextMonth.format('MM'),
                    year: nextMonth.format('YYYY'),
                    product: product,
                    field: 'month_right'
                }));
                
                // Mark this month as fetched for right calendar
                setFetchedRightMonths(prev => ({
                    ...prev,
                    [nextMonthKey]: true
                }));
            }
        }
    }, [dispatch, product, location, nextMonth, fetchedRightMonths, showTwoMonths]);

    // hide the navigation buttons
    const HiddenNavigation = () => null;

    if(!product) {
        return null;
    }

    return (
        <QVContainer borderRadius={8} sx={{ width: '100%', height: '100%', maxWidth: '100%', overflow: 'visible', display: 'flex' }}>
            <Stack direction='column' spacing={2} sx={{ width: '100%', height: '100%' }}>
                <Box ref={calendarRef} position="relative" width="100%" height="100%" onMouseLeave={handleDayHoverOut}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={pickersLocale.adapterLocale}
                        localeText={pickersLocale.localeText}
                    >
                        <Grid container columnSpacing={2} rowSpacing={2} sx={{ width: '100%', height: '100%', m: 0 }}>
                            <Grid item xs={showTwoMonths ? 6 : 12} sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, height: '100%', pl: '16px !important', pt: '0 !important', pr: showTwoMonths ? 1 : 0 }}>
                                <StyledDateCalendar 
                                    value={currentMonth}
                                    views={['day']}
                                    readOnly
                                    onMonthChange={(newMonth) => {
                                        setCurrentMonth(newMonth.startOf('month'));
                                    }}
                                    slots={{
                                        leftArrowIcon: showTwoMonths ? HiddenNavigation : undefined,
                                        rightArrowIcon: showTwoMonths ? HiddenNavigation : undefined,
                                        day: (props) => renderWeekPickerDay(
                                            props,
                                            true,
                                            product?.month_left?.[product.product_id] || {},
                                            shouldDisableDate,
                                            handleDayHover,
                                            handleDayHoverOut,
                                            { availabilityDisplayMode: 'segments', stretchDayCell: true }
                                        )
                                    }}
                                />
                            </Grid>

                            {showTwoMonths &&
                                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, height: '100%', pl: '8px !important', pt: '0 !important' }}>
                                    <StyledDateCalendar
                                        value={nextMonth}
                                        views={['day']}
                                        readOnly
                                        onMonthChange={(newMonth) => {
                                            // when the second calendar month changes, update the first calendar
                                            const updatedCurrentMonth = newMonth.subtract(1, 'month');
                                            setCurrentMonth(updatedCurrentMonth);
                                        }}
                                        slots={{
                                            day: (props) => renderWeekPickerDay(
                                                props,
                                                true,
                                                product?.month_right?.[product.product_id] || {},
                                                shouldDisableDate,
                                                handleDayHover,
                                                handleDayHoverOut,
                                                { availabilityDisplayMode: 'segments', stretchDayCell: true }
                                            )
                                        }}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </LocalizationProvider>
                    
                    <DateInfoPopover 
                        day={hoveredDay} 
                        position={popoverPosition}
                        isVisible={hoveredDay !== null}
                        segments={segmentsForHoveredDay}
                    />
                </Box>
            </Stack>
        </QVContainer>
    );
};

export default AvailabilityCalendar;
